/**
 * What the correction modal's form records — the gesture, the body it posts, what blocks it — read
 * off the form alone. Kept out of the component so that every combination can be checked without a
 * browser: three gestures, eight classes, each field type and the reopening of a correction.
 */
import JSON5 from "json5";
import type { TypeEnum } from "@api/src/types/few-schot-schema";
import type { FieldGesture } from "@api/src/types/patient";
import {
  CLASSES_ASSERTABLE_WITHOUT_A_VALUE,
  CLASSES_REQUIRING_PROSE,
  CLASSES_REQUIRING_TARGET_FIELD,
  CLASSES_WITH_OPTIONAL_PROSE,
  type CorrectionErrorClass,
  type JustificationSource,
} from "@app/types/correction";
import { isSameFieldValue, readBoolean } from "@app/utils/resolve-field-value";

const extractStandaloneNumber = (raw: string): number | null => {
  const matches = raw.match(/(?<![A-Za-z])-?\d+(?:[.,]\d+)?(?![A-Za-z])/g);
  if (!matches || matches.length === 0) return null;
  const parsed = parseFloat(matches[matches.length - 1].replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeDateString = (raw: string): string => {
  const trimmed = raw.trim();
  const isoMatch = trimmed.match(/\d{4}-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[0];
  const dmySlashMatch = trimmed.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmySlashMatch) {
    const [, dd, mm, yyyy] = dmySlashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }
  const dmyDashMatch = trimmed.match(/(\d{2})-(\d{2})-(\d{4})/);
  if (dmyDashMatch) {
    const [, dd, mm, yyyy] = dmyDashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }
  return trimmed;
};

/**
 * Lit une valeur comme un booléen, ou `null` quand elle n'en dit pas un.
 *
 * Les trois lecteurs booléens de cette modale — l'affichage du groupe radio et les deux chemins
 * d'enregistrement — comparaient chacun avec `=== true`. Sur une valeur que l'éditeur ne sait pas
 * afficher, l'affichage rendait « rien » (ses trois états : oui / non / rien) pendant que
 * l'enregistrement rendait `false`. Valider sans rien changer gravait donc une affirmation
 * clinique que personne n'avait faite.

 * Une liste à un élément EST cet élément : c'est ce que `[true]` veut dire, et refuser de le lire
 * perd une réponse. Tout le reste vaut `null` — inconnu — parce qu'un booléen n'a pas de valeur par
 * défaut : « non » est une affirmation, pas une absence.
 */
export const asBoolean = (value: unknown): boolean | null => {
  if (Array.isArray(value))
    return value.length === 1 ? asBoolean(value[0]) : null;
  // The server's words: « Oui » shown on the row is a yes here too.
  return readBoolean(value);
};

// How an absent value is serialised in the POST body, on both sides of it.
export const getEmptyValue = (type: TypeEnum | undefined): unknown => {
  switch (type) {
    case "array_string":
    case "array_enum":
    case "scores":
      return [];
    // Absence, never `false` / `0`: those are answers. Sent as the old value of an empty
    // field, a correction to « Non » (or 0) reached the server as unchanged, which then
    // refused its reason as a flag's.
    case "number":
    case "boolean":
      return "";
    case "date":
      return "";
    case "string":
      return "";
    default:
      return "";
  }
};

/** A value read the way its type compares it: the number in « 65 kg », an ISO date, a parsed list. */
export function processFieldValue(
  type: TypeEnum | undefined,
  processedValue: unknown,
): unknown {
  if (type === "number") {
    if (typeof processedValue === "number") return processedValue;
    if (typeof processedValue === "string") {
      const n = extractStandaloneNumber(processedValue);
      return n ?? processedValue.trim();
    }
    return processedValue;
  }
  if (type === "date") {
    if (typeof processedValue === "string")
      return normalizeDateString(processedValue);
    return processedValue;
  }
  if (type === "boolean") {
    return asBoolean(processedValue);
  }
  if (type === "array_string" || type === "array_enum" || type === "scores") {
    try {
      if (Array.isArray(processedValue)) return processedValue;
      return JSON5.parse(processedValue as string);
    } catch {
      return processedValue;
    }
  }
  return processedValue;
}

/**
 * The correction a reopened modal starts from (PT-165): the log row behind the gesture the row
 * shows. Only one the value on screen still is — a correction a later document stacked on is
 * shown, never revised.
 */
export interface RevisedCorrection {
  verdict: "corrected" | "flagged";
  /** `old_value` as the log stores it, sent back as is: the revision replaces the same value. */
  replacedRaw: string;
  /** The same value peeled by the server, to name it. */
  replacedValue: unknown;
  errorClass: CorrectionErrorClass | null;
  justificationSource: JustificationSource | null;
  notes: string;
  targetField: string | null;
  /** The clinical day it was anchored on: a revision stays on it, wherever the cursor is. */
  anchor: string;
  /** The document it named; `null` stays `null`, so the revision is placed like the original. */
  scopeDocId: string | null;
  madeAt: string;
  authorName: string | null;
  authorIsDoctor: boolean;
  byReader: boolean;
}

/** What the modal knew when it opened. */
export interface CorrectionOpening {
  type: TypeEnum | undefined;
  /** The value on screen at opening — what a confirmation confirms. */
  baselineValue: unknown;
  /** The value as the page showed it, when the editor reads it in other terms (a reality as its
   *  label). A gesture that leaves the value as it was records this one, as the check does. */
  shownValue?: unknown;
  /** A dossier stands behind the field, so a gesture is journaled. */
  recordsFeedback: boolean;
  /** The clinical day the surface proposes (the cursor), `null` off the dated page. */
  effectiveDate: string | null;
  /** The document the surface names, `null` outside an episode's moment. */
  scopeDocId: string | null;
  revised: RevisedCorrection | null;
}

export interface CorrectionForm {
  draft: unknown;
  /** What the doctor clicked, or what the revised correction carried — never the suggestion. */
  errorClass: CorrectionErrorClass | null;
  explanation: string;
  targetField: string | null;
  clinicalDate: string;
}

/** The form as the modal opens it: blank, or the revised correction as it was made. */
export function openingForm(opening: CorrectionOpening): CorrectionForm {
  const revised = opening.revised;
  return {
    draft: opening.baselineValue,
    errorClass: revised?.errorClass ?? null,
    explanation: revised?.notes ?? "",
    targetField: revised?.targetField ?? null,
    clinicalDate: revised?.anchor ?? opening.effectiveDate ?? "",
  };
}

export interface CorrectionReading {
  valueChanged: boolean;
  /** A correction reopened with its value kept: saving records it again, with what was edited. */
  revisesCorrection: boolean;
  effectiveErrorClass: CorrectionErrorClass | null;
  justificationActive: boolean;
  gestureToRecord: FieldGesture;
  needsProse: boolean;
  offersOptionalProse: boolean;
  showsProse: boolean;
  needsTargetField: boolean;
  reasonNeedsAValue: boolean;
  justificationBlocked: boolean;
  /** Something would be lost by closing. */
  isDirty: boolean;
  /** A reopened correction saved untouched would only write it twice. */
  nothingToRevise: boolean;
  /** The line under the form that says what saving records. */
  summary: "corrected" | "revised" | "flagged" | "validated";
  /** Clicking the chosen reason again removes it (the way back to a confirmation). */
  reasonToggles: boolean;
  hints: {
    /** « Facultatif : laissez ce bloc vide pour confirmer… » */
    optionalBlock: boolean;
    /** « Cliquez à nouveau sur cette raison pour la retirer… » */
    clickAgainToRemove: boolean;
    /** « Choisissez la raison de l'erreur… » */
    chooseReason: boolean;
  };
}

/**
 * Le geste, déduit du formulaire et jamais du bouton.
 *
 * Trois gestes, un seul enregistrement. Le pied de modale en portait trois : « Signaler
 * sans corriger » armait un mode, et le bouton principal changeait alors de libellé —
 * si bien qu'un clic sur le premier semblait déclencher le second. Ce qu'un médecin
 * remplit dit déjà lequel des trois il pose :
 *
 *   valeur inchangée + aucune raison  → il confirme    (``validated``)
 *   valeur inchangée + une raison     → il signale     (``flagged``)
 *   valeur modifiée                   → il corrige     (``corrected``)
 *
 * On a reopened correction (PT-165), value unchanged → corrects again: the doctor is revising the
 * reason or the text, not saying their own answer is wrong.
 */
export function readCorrectionForm(
  opening: CorrectionOpening,
  form: CorrectionForm,
  suggestedClass: CorrectionErrorClass | null,
): CorrectionReading {
  const { type, baselineValue, recordsFeedback, revised } = opening;
  const { errorClass, explanation, targetField } = form;
  // Le médecin a-t-il effectivement changé quelque chose ?
  //
  // Le crayon servait deux gestes que rien ne distinguait : redresser une valeur, et
  // ouvrir la modale pour lire puis enregistrer sans rien toucher. Les deux partaient avec
  // ``is_correct: false``, donc le second était relu comme un désaccord. Mesuré en
  // production : 1 135 lignes, 24,8 % de ce que le laboratoire comptait comme corrections,
  // dont l'écrasante majorité sont des « Travailleur » → « Travailleur ».
  //
  // Enregistrer sans changement dit exactement ce que dit le check : la valeur affichée est
  // la bonne. C'est donc une validation, et c'est ce qui part maintenant.
  const valueChanged = !isSameFieldValue(
    processFieldValue(type, form.draft),
    processFieldValue(type, baselineValue),
  );
  const revisesCorrection = revised?.verdict === "corrected" && !valueChanged;
  const corrects = valueChanged || revisesCorrection;
  // La classe effectivement portée par l'enregistrement.
  //
  // La proposition de la machine ne s'applique qu'à une correction. Sur une valeur
  // inchangée, une classe vaut signalement — et un signalement que personne n'a demandé
  // serait la pire chose que cette modale puisse écrire.
  const effectiveErrorClass = errorClass ?? (corrects ? suggestedClass : null);
  const justificationActive = corrects || errorClass !== null;
  const gestureToRecord: FieldGesture = corrects
    ? "corrected"
    : errorClass
      ? "flagged"
      : "validated";
  const needsProse = effectiveErrorClass
    ? CLASSES_REQUIRING_PROSE.includes(effectiveErrorClass)
    : false;
  // Shown like the required box, but never blocks the save.
  const offersOptionalProse = effectiveErrorClass
    ? CLASSES_WITH_OPTIONAL_PROSE.includes(effectiveErrorClass)
    : false;
  const needsTargetField = effectiveErrorClass
    ? CLASSES_REQUIRING_TARGET_FIELD.includes(effectiveErrorClass)
    : false;
  // Une raison qui suppose la bonne valeur, cochée sans qu'aucune valeur ne soit donnée :
  // le serveur la refuse, et l'écran doit le dire — en nommant la sortie, pas en grisant
  // le bouton sous un message qui parle d'autre chose.
  const reasonNeedsAValue =
    !corrects &&
    effectiveErrorClass !== null &&
    !CLASSES_ASSERTABLE_WITHOUT_A_VALUE.includes(effectiveErrorClass);
  // What the modal requires before saving: the error class first — a correction without
  // one is a right value nobody knows what to learn from — then the prose or the target
  // field the class requires. Optional prose never blocks.
  //
  // Le serveur, lui, n'exige toujours rien : une correction est une vérité clinique et sa
  // classe une donnée d'entraînement, donc le write path accepte une ligne non classée
  // (client ancien, historique). C'est l'interface qui pose l'exigence, là où elle peut
  // encore montrer les huit choix.
  //
  // ``recordsFeedback`` en tête : sans dossier derrière le champ, aucun sélecteur n'est
  // affiché, donc exiger une classe condamnerait le bouton.
  //
  // ``justificationActive`` ensuite : une classe d'erreur décrit POURQUOI l'extraction
  // s'est trompée. Confirmer la valeur affichée ne rapporte aucune erreur, donc rien à
  // classer — exiger une classe y pousserait à en cocher une au hasard, ce qui polluerait
  // la seule colonne dont la passe d'entraînement dépend.
  const justificationBlocked =
    recordsFeedback &&
    justificationActive &&
    (!effectiveErrorClass ||
      (needsProse && !explanation.trim()) ||
      (needsTargetField && !targetField) ||
      reasonNeedsAValue);
  // Ce qui serait perdu si la modale se fermait maintenant. Tout ce qu'un médecin a pu
  // saisir compte, pas seulement la valeur : une raison cochée et une explication écrite
  // sont un travail qu'il ne doit pas découvrir disparu. Comparé à ce que l'ouverture a
  // posé : une correction rouverte arrive remplie.
  const opened = openingForm(opening);
  const isDirty =
    valueChanged ||
    errorClass !== opened.errorClass ||
    explanation.trim() !== opened.explanation.trim() ||
    targetField !== opened.targetField ||
    form.clinicalDate !== opened.clinicalDate;
  const reasonToggles = !corrects;
  return {
    valueChanged,
    revisesCorrection,
    effectiveErrorClass,
    justificationActive,
    gestureToRecord,
    needsProse,
    offersOptionalProse,
    showsProse: needsProse || offersOptionalProse,
    needsTargetField,
    reasonNeedsAValue,
    justificationBlocked,
    isDirty,
    nothingToRevise: revised !== null && !isDirty,
    summary: valueChanged
      ? "corrected"
      : revisesCorrection
        ? "revised"
        : errorClass
          ? "flagged"
          : "validated",
    reasonToggles,
    hints: {
      optionalBlock: !corrects,
      clickAgainToRemove: reasonToggles && errorClass !== null && !reasonNeedsAValue,
      chooseReason: corrects && !effectiveErrorClass,
    },
  };
}

/**
 * D'où vient la classe enregistrée. Une classe jamais regardée est une preuve plus
 * faible qu'une classe arbitrée, et la passe d'entraînement doit pouvoir les
 * distinguer : sans cette distinction, un défaut de pré-remplissage se propagerait
 * dans le corpus avec l'autorité d'un clinicien.
 *
 * A reopened correction whose class is kept keeps the provenance it was recorded with: reopening
 * it to edit the text arbitrates nothing.
 */
export function justificationSourceOf(
  opening: CorrectionOpening,
  form: CorrectionForm,
  reading: CorrectionReading,
  suggestedClass: CorrectionErrorClass | null,
): JustificationSource | null {
  const { effectiveErrorClass } = reading;
  if (!effectiveErrorClass) return null;
  const revised = opening.revised;
  if (
    revised?.justificationSource &&
    revised.errorClass === effectiveErrorClass
  )
    return revised.justificationSource;
  if (!suggestedClass) return "manual";
  // La classe vient de la proposition, que personne n'a cliquée : ``errorClass`` ne
  // porte que les choix du médecin. Sans ce distinguo, un pré-remplissage accepté par
  // inaction serait indiscernable d'un accord explicite.
  if (form.errorClass === null) return "auto";
  if (form.errorClass !== suggestedClass) return "overridden";
  return "confirmed";
}

/** The value a gesture records: the draft, unless it leaves the value as the page showed it. */
export function recordedValue(
  opening: CorrectionOpening,
  reading: CorrectionReading,
  processedValue: unknown,
): unknown {
  const keepsShown =
    opening.shownValue !== undefined &&
    !reading.valueChanged &&
    !reading.revisesCorrection;
  return keepsShown ? opening.shownValue : processedValue;
}

// A type rather than an interface: the API client takes a plain record.
export type CorrectionBody = {
  field: string;
  old_value: string;
  new_value: string | null;
  is_correct: boolean;
  notes: string | null;
  effective_date: string | null;
  scope_doc_id: string | null;
  error_class: CorrectionErrorClass | null;
  justification_source: JustificationSource | null;
  target_field: string | null;
};

/**
 * What `POST /patient/{id}/feedback` receives for *processedValue*, the draft read for saving.
 *
 * Une confirmation ne porte ni classe, ni prose, ni citation, ni champ cible : ces
 * quatre colonnes décrivent une erreur, et il n'y en a pas eu. Les envoyer quand
 * même ferait entrer dans le corpus d'entraînement une classe que le médecin n'a
 * jamais arbitrée — la suggestion automatique est pré-remplie à l'ouverture.
 */
export function correctionBody(
  field: string,
  opening: CorrectionOpening,
  form: CorrectionForm,
  reading: CorrectionReading,
  suggestedClass: CorrectionErrorClass | null,
  processedValue: unknown,
): CorrectionBody {
  const { type, baselineValue, revised } = opening;
  const { justificationActive, valueChanged, revisesCorrection } = reading;
  const toStr = (v: unknown): string =>
    typeof v === "string" ? v : JSON.stringify(v ?? "");
  const recorded = recordedValue(opening, reading, processedValue);
  const newValue =
    recorded === null ? null : toStr(recorded ?? getEmptyValue(type));
  const shown =
    opening.shownValue !== undefined ? opening.shownValue : baselineValue;
  // A flag leaves the value as shown, so both sides carry that one value, spelled once: the
  // server compares spellings, and read `[true]` → `true` or `[]` → nothing as a correction.
  // A value neither the editor nor the server can read is nothing on both sides: kept raw on
  // one, the flag was logged as a correction emptying the field.
  const flagsShownValue = justificationActive && !valueChanged;
  return {
    field,
    // La valeur affichée à l'ouverture, pas la colonne agrégée : sur un deuxième
    // geste, celle-ci n'est plus ce que le médecin corrige. On a revision, the value the
    // correction replaced: the value shown is the correction itself.
    old_value:
      revisesCorrection && revised
        ? revised.replacedRaw
        : flagsShownValue
          ? (newValue ?? "")
          : toStr(shown ?? getEmptyValue(type)),
    new_value: newValue,
    // Le geste, et non le bouton : enregistrer sans rien changer confirme la valeur,
    // sauf si le médecin a explicitement signalé — auquel cas le désaccord part comme
    // tel, avec sa raison.
    is_correct: !justificationActive,
    // Sent only where the box is shown; an empty optional box sends null.
    notes:
      justificationActive && reading.showsProse
        ? form.explanation.trim() || null
        : null,
    // Jour clinique décrit par la correction. null = état courant : le serveur
    // l'ancre alors au jour de la soumission.
    effective_date: form.clinicalDate || null,
    // A revision names the document the correction named, so it is placed where it was.
    scope_doc_id: revised ? revised.scopeDocId : opening.scopeDocId,
    // Pourquoi l'extraction s'est trompée, et comment ce verdict a été atteint.
    error_class: justificationActive ? reading.effectiveErrorClass : null,
    justification_source: justificationActive
      ? justificationSourceOf(opening, form, reading, suggestedClass)
      : null,
    // Le champ auquel la valeur appartenait. Seule ``WRONG_FIELD`` en porte un : une
    // classe qui dit « pas ici » sans dire « là » ne répare rien, puisque le défaut
    // est la frontière entre deux descriptions de champ.
    target_field:
      justificationActive && reading.needsTargetField ? form.targetField : null,
  };
}
